import React, { useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from 'react-redux';
import { Box, Grid, Paper, TextField, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import CircularLoading from '../components/CircularLoading';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import theme from 'styles/tableStyle';
import { SECONDORY_COLOR, formatCurrency, labelText } from '../common/sharedFunctions';

const parseAmount = (value) => {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
};

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const isPrivateClientBooking = (booking) => !!(
  booking && (booking.bookingSource === 'fleet_admin_client' || booking.visibility === 'private')
);

const getBookingDate = (booking) => firstPresent(
  booking.bookingDate,
  booking.createdAt,
  booking.tripdate,
  booking.tripDate,
  booking.bookLaterDate
);

const getFleetOwnerUid = (booking) => firstPresent(
  booking.fleetAdminUid,
  booking.fleetadminUid,
  booking.fleetadmin,
  booking.createdByUid,
  booking.createdBy
) || '';

const getUserDisplayName = (user) => {
  if (!user) return '';
  const personName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return firstPresent(
    user.companyName,
    user.company_name,
    user.businessName,
    user.business_name,
    user.fleetAdminName,
    personName,
    user.email
  ) || '';
};

export default function PrivateBookingReports({ reportType }) {
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const userdata = useSelector((state) => state.usersdata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fleetAdminFilter, setFleetAdminFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);

  const profile = auth.profile || {};
  const isAdmin = profile.usertype === 'admin';
  const isFleetAdmin = profile.usertype === 'fleetadmin';
  const isCommissionReport = reportType === 'commission';
  const canViewReport = isCommissionReport ? isAdmin : (isAdmin || isFleetAdmin);
  const users = useMemo(() => (userdata && Array.isArray(userdata.users) ? userdata.users : []), [userdata]);

  const fleetAdminNames = useMemo(() => {
    return users.reduce((names, user) => {
      const uid = user && (user.id || user.uid);
      if (uid && user.usertype === 'fleetadmin') names[uid] = getUserDisplayName(user);
      return names;
    }, {});
  }, [users]);

  const rows = useMemo(() => {
    const bookings = bookinglistdata && Array.isArray(bookinglistdata.bookings)
      ? bookinglistdata.bookings
      : [];

    return bookings
      .filter(isPrivateClientBooking)
      .filter((booking) => {
        if (isAdmin) return true;
        return isFleetAdmin && `${getFleetOwnerUid(booking)}` === `${profile.uid || ''}`;
      })
      .map((booking) => {
        const fleetAdminUid = getFleetOwnerUid(booking);
        const rawDate = getBookingDate(booking);
        const parsedDate = rawDate ? moment(rawDate) : null;
        const assignedVehicleName = firstPresent(booking.assignedVehicleName, booking.vehicle_name, booking.assignedVehicle) || '';
        const assignedVehiclePlate = firstPresent(booking.assignedVehiclePlate, booking.vehicle_number, booking.driverVehicleNo) || '';
        return {
          id: booking.id,
          bookingReference: firstPresent(booking.reference, booking.bookingNumber, booking.id) || '',
          bookingDate: parsedDate && parsedDate.isValid() ? parsedDate.valueOf() : null,
          fleetAdminUid,
          fleetAdminName: firstPresent(booking.fleetadminName, booking.fleetAdminName, fleetAdminNames[fleetAdminUid], fleetAdminUid) || '',
          clientName: firstPresent(booking.clientName, booking.customer_name, booking.customerName) || '',
          clientContact: firstPresent(booking.clientContact, booking.customer_contact, booking.customerContact, booking.customer_mobile) || '',
          truckingCharge: parseAmount(booking.truckingChargeToClient),
          commissionType: booking.superAdminCommissionType || '',
          commissionValue: parseAmount(booking.superAdminCommissionValue),
          commissionAmount: parseAmount(booking.superAdminCommissionAmount),
          additionalFeesTotal: parseAmount(firstPresent(booking.totalAdditionalFeeAmount, booking.additionalFeesTotal)),
          fleetAdminNetAmount: parseAmount(booking.fleetAdminNetAmount),
          bookingStatus: booking.status || '',
          assignedDriver: firstPresent(booking.assignedDriverName, booking.driver_name, booking.assignedDriver, booking.driver) || '',
          assignedVehicle: [assignedVehicleName, assignedVehiclePlate].filter(Boolean).join(' - ')
        };
      });
  }, [bookinglistdata, fleetAdminNames, isAdmin, isFleetAdmin, profile.uid]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    if (startDate && (!row.bookingDate || row.bookingDate < moment(startDate).startOf('day').valueOf())) return false;
    if (endDate && (!row.bookingDate || row.bookingDate > moment(endDate).endOf('day').valueOf())) return false;
    if (fleetAdminFilter && row.fleetAdminUid !== fleetAdminFilter) return false;
    if (statusFilter && row.bookingStatus !== statusFilter) return false;
    return true;
  }), [rows, startDate, endDate, fleetAdminFilter, statusFilter]);

  const totals = useMemo(() => filteredRows.reduce((summary, row) => ({
    truckingCharge: summary.truckingCharge + row.truckingCharge,
    commissionAmount: summary.commissionAmount + row.commissionAmount,
    additionalFeesTotal: summary.additionalFeesTotal + row.additionalFeesTotal,
    fleetAdminNetAmount: summary.fleetAdminNetAmount + row.fleetAdminNetAmount,
    bookingCount: summary.bookingCount + 1
  }), {
    truckingCharge: 0,
    commissionAmount: 0,
    additionalFeesTotal: 0,
    fleetAdminNetAmount: 0,
    bookingCount: 0
  }), [filteredRows]);

  const fleetAdminOptions = useMemo(() => {
    const options = {};
    rows.forEach((row) => {
      if (row.fleetAdminUid) options[row.fleetAdminUid] = row.fleetAdminName;
    });
    return Object.keys(options).map((uid) => ({ uid, name: options[uid] }));
  }, [rows]);

  const statusOptions = useMemo(() => Array.from(new Set(rows.map((row) => row.bookingStatus).filter(Boolean))).sort(), [rows]);
  const currency = (value) => formatCurrency(value || 0, settings);
  const rateValue = (row) => row.commissionType === 'percentage' ? `${row.commissionValue}%` : currency(row.commissionValue);

  const commonColumns = [
    { title: labelText(t, 'booking_ref', 'Booking ID'), field: 'bookingReference' },
    { title: labelText(t, 'booking_date', 'Booking Date'), field: 'bookingDate', render: (row) => row.bookingDate ? moment(row.bookingDate).format('lll') : '' },
    ...(isCommissionReport || isAdmin ? [{ title: labelText(t, 'fleet_admin', 'Fleet Admin'), field: 'fleetAdminName' }] : []),
    { title: labelText(t, 'client_name', 'Client Name'), field: 'clientName' },
    { title: labelText(t, 'client_contact', 'Client Contact'), field: 'clientContact' },
    { title: labelText(t, 'trucking_charge_to_client', 'Trucking Charge'), field: 'truckingCharge', render: (row) => currency(row.truckingCharge) },
    ...(isCommissionReport ? [
      { title: labelText(t, 'super_admin_commission_type', 'Commission Type'), field: 'commissionType' },
      { title: labelText(t, 'super_admin_commission_value', 'Commission Value'), field: 'commissionValue', render: rateValue }
    ] : []),
    { title: labelText(t, 'super_admin_commission_amount', isCommissionReport ? 'Commission Amount' : 'Commission Deducted'), field: 'commissionAmount', render: (row) => currency(row.commissionAmount) },
    { title: labelText(t, 'additional_fees_total', 'Total Additional Fees'), field: 'additionalFeesTotal', render: (row) => currency(row.additionalFeesTotal) },
    { title: labelText(t, 'fleet_admin_net_amount', 'Fleet Admin Net Amount'), field: 'fleetAdminNetAmount', render: (row) => currency(row.fleetAdminNetAmount) },
    { title: labelText(t, 'booking_status_web', 'Status'), field: 'bookingStatus' },
    { title: labelText(t, 'assigned_driver', 'Assigned Driver'), field: 'assignedDriver' },
    { title: labelText(t, 'assigned_vehicle', 'Assigned Vehicle'), field: 'assignedVehicle' }
  ];

  if (!canViewReport) {
    return <Typography>{labelText(t, 'not_authorized', 'You are not authorized to view this report.')}</Typography>;
  }

  if (bookinglistdata.loading) return <CircularLoading />;

  const summaryItems = [
    [labelText(t, 'total_trucking_charge', 'Total Trucking Charge'), currency(totals.truckingCharge)],
    [labelText(t, 'total_commission_amount', isCommissionReport ? 'Total Commission Amount' : 'Total Commission Deducted'), currency(totals.commissionAmount)],
    [labelText(t, 'total_additional_fees', 'Total Additional Fees'), currency(totals.additionalFeesTotal)],
    [labelText(t, 'total_fleet_admin_net_amount', 'Total Fleet Admin Net Amount'), currency(totals.fleetAdminNetAmount)],
    [labelText(t, 'booking_count', 'Booking Count'), totals.bookingCount]
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box>
        <Paper sx={{ p: 2, mb: 2, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}` }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth type="date" label={labelText(t, 'start_date', 'Start Date')} value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth type="date" label={labelText(t, 'end_date', 'End Date')} value={endDate} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            {isAdmin ? (
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth select SelectProps={{ native: true }} label={labelText(t, 'fleet_admin', 'Fleet Admin')} value={fleetAdminFilter} onChange={(event) => setFleetAdminFilter(event.target.value)}>
                  <option value="">{labelText(t, 'all', 'All')}</option>
                  {fleetAdminOptions.map((option) => <option key={option.uid} value={option.uid}>{option.name}</option>)}
                </TextField>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth select SelectProps={{ native: true }} label={labelText(t, 'booking_status_web', 'Status')} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">{labelText(t, 'all', 'All')}</option>
                {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {summaryItems.map(([summaryLabel, value]) => (
            <Grid item xs={12} sm={6} md key={summaryLabel}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="body2" color="text.secondary">{summaryLabel}</Typography>
                <Typography variant="h6">{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <MaterialTable
          title={isCommissionReport
            ? labelText(t, 'private_booking_commission_report', 'Super Admin Private Booking Commission Report')
            : labelText(t, 'private_booking_fleet_earnings_report', 'Fleet Admin Private Booking Earnings Report')}
          columns={commonColumns}
          data={filteredRows}
          onRowClick={(event, row) => setSelectedRow(row.tableData.id)}
          options={{
            exportButton: true,
            rowStyle: (row) => ({
              backgroundColor: selectedRow === row.tableData.id ? '#EEE' : '#FFF',
              border: '1px solid rgba(224, 224, 224, 1)'
            }),
            ...TableStyle()
          }}
          localization={localization(t)}
          style={{
            direction: i18n.dir() === 'rtl' ? 'rtl' : 'ltr',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`
          }}
        />
      </Box>
    </ThemeProvider>
  );
}
